<template>
  <div class="stock-trend page-shell">
    <div class="toolbar">
      <span class="toolbar-label">股票</span>
      <el-select
        v-model="selectedCodes"
        size="small"
        multiple
        filterable
        collapse-tags
        placeholder="请选择重点关注股票"
        style="width: 360px"
      >
        <el-option
          v-for="item in focusStocks"
          :key="item.code"
          :label="`${item.name}（${item.code}）`"
          :value="item.code"
          :disabled="isStockDisabled(item.code)"
        />
      </el-select>

      <span class="toolbar-label">区间</span>
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        size="small"
        value-format="yyyy-MM-dd"
        range-separator="至"
        start-placeholder="开始"
        end-placeholder="结束"
        :picker-options="pickerOptions"
      />

      <el-button type="primary" size="small" :loading="loading" @click="onQuery">
        查询
      </el-button>
    </div>

    <el-alert
      v-if="!focusStocks.length && !loadingFocus"
      type="warning"
      :closable="false"
      show-icon
      class="sync-alert"
      title="尚未配置重点关注股票，请先到「数据配置」从 Top500 中添加。"
    />

    <el-alert
      v-if="result?.adjusted"
      type="info"
      :closable="false"
      show-icon
      class="sync-alert"
    >
      <template slot="title">
        已对齐至最近交易日：{{ result.start }} → {{ result.end }}
        （原选择 {{ result.requestedStart }} → {{ result.requestedEnd }}）
      </template>
    </el-alert>

    <div v-if="result" class="stats-bar">
      <span>{{ result.methodLabel }} · {{ result.start }} → {{ result.end }}</span>
      <span
        v-for="item in result.series"
        :key="item.code"
        class="stat-item"
      >
        {{ item.label }}
        {{ formatPct(item.periodReturn) }}%
      </span>
    </div>

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      class="error-alert"
    />

    <div
      ref="chart"
      class="chart-box"
      v-loading="loading"
      element-loading-text="查询中..."
    />

    <div v-if="queried && !loading && !error && !result?.series?.length" class="empty-tip">
      无数据，请确认股票与日期区间
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts/core'
import { LineChart } from 'echarts/charts'
import {
  TitleComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  DataZoomComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { fetchFocusStocks, fetchStockTrend } from '../utils/focusApi.js'

echarts.use([
  LineChart,
  TitleComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  DataZoomComponent,
  CanvasRenderer,
])

const DEFAULT_START_DATE = '2026-06-30'
const MAX_STOCKS = 8

function formatDate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function todayDate() {
  return formatDate(new Date())
}

export default {
  name: 'StockTrend',
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      focusStocks: [],
      selectedCodes: [],
      dateRange: [DEFAULT_START_DATE, todayDate()],
      loading: false,
      loadingFocus: false,
      queried: false,
      error: '',
      result: null,
      chart: null,
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() > Date.now()
        },
      },
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.refreshFocusStocks()
        this.$nextTick(() => this.chart?.resize())
      }
    },
  },
  async created() {
    await this.refreshFocusStocks()
    if (this.selectedCodes.length) {
      await this.onQuery()
    }
  },
  mounted() {
    this.initChart()
    window.addEventListener('resize', this.onResize, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    isStockDisabled(code) {
      return (
        this.selectedCodes.length >= MAX_STOCKS
        && !this.selectedCodes.includes(code)
      )
    },
    formatPct(v) {
      if (v == null || v === '') return '--'
      const n = Number(v)
      if (!Number.isFinite(n)) return '--'
      return `${n > 0 ? '+' : ''}${n.toFixed(2)}`
    },
    async refreshFocusStocks() {
      this.loadingFocus = true
      try {
        const list = await fetchFocusStocks()
        this.focusStocks = list
        const valid = new Set(list.map((item) => item.code))
        this.selectedCodes = this.selectedCodes.filter((code) => valid.has(code))
        if (!this.selectedCodes.length && list.length) {
          this.selectedCodes = list.slice(0, Math.min(3, list.length)).map((item) => item.code)
        }
      } catch (err) {
        this.error = err.message || '加载重点关注失败'
      } finally {
        this.loadingFocus = false
      }
    },
    initChart() {
      if (!this.$refs.chart) return
      this.chart = echarts.init(this.$refs.chart)
      this.chart.setOption({
        title: {
          text: '请选择股票后查询',
          left: 'center',
          top: 'middle',
          textStyle: { color: '#909399', fontSize: 14, fontWeight: 400 },
        },
      })
    },
    onResize() {
      this.chart?.resize()
    },
    renderChart(result) {
      if (!this.chart) this.initChart()
      if (!this.chart || !result) return

      const dates = result.dates || []
      const series = (result.series || []).map((item) => {
        const byDate = new Map(item.points.map((p) => [p.date, p.index]))
        return {
          name: item.label,
          type: 'line',
          showSymbol: false,
          smooth: true,
          data: dates.map((d) => byDate.get(d) ?? null),
        }
      })

      this.chart.setOption({
        title: { show: false },
        color: ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#00C2B3', '#8B5CF6', '#F472B6'],
        tooltip: {
          trigger: 'axis',
          valueFormatter: (v) => (v == null ? '--' : Number(v).toFixed(2)),
        },
        legend: {
          type: 'scroll',
          top: 0,
        },
        grid: {
          left: 48,
          right: 24,
          top: 48,
          bottom: 56,
        },
        dataZoom: [
          { type: 'inside', start: 0, end: 100 },
          { type: 'slider', height: 18, bottom: 8 },
        ],
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLabel: {
            formatter(value) {
              const parts = String(value).split('-')
              return parts.length === 3 ? `${parts[1]}-${parts[2]}` : value
            },
          },
        },
        yAxis: {
          type: 'value',
          scale: true,
          name: '指数',
          axisLabel: {
            formatter: (v) => Number(v).toFixed(0),
          },
        },
        series,
      }, true)
      this.$nextTick(() => this.chart?.resize())
    },
    async onQuery() {
      if (!this.focusStocks.length) {
        this.$message.warning('请先到「数据配置」添加重点关注股票')
        return
      }
      if (!this.selectedCodes.length) {
        this.$message.warning('请至少选择一只股票')
        return
      }
      if (this.selectedCodes.length > MAX_STOCKS) {
        this.$message.warning(`最多同时对比 ${MAX_STOCKS} 只股票`)
        return
      }
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$message.warning('请选择日期区间')
        return
      }

      this.loading = true
      this.error = ''
      this.queried = true
      try {
        const data = await fetchStockTrend({
          codes: this.selectedCodes,
          start: this.dateRange[0],
          end: this.dateRange[1],
        })
        this.result = data
        this.renderChart(data)
      } catch (err) {
        this.result = null
        this.error = err.message || '查询失败'
        this.chart?.clear()
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.stock-trend {
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 120px);
}

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.toolbar-label {
  font-size: 14px;
  color: #606266;
}

.stats-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #606266;
}

.stat-item {
  white-space: nowrap;
}

.sync-alert,
.error-alert {
  margin-bottom: 12px;
}

.chart-box {
  flex: 1;
  min-height: 420px;
  width: 100%;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
}

.empty-tip {
  padding: 24px 0;
  color: #909399;
  text-align: center;
}
</style>
